<script setup lang="ts">
import { ref, onMounted } from "vue";
import axios from "axios";
import dayjs from "dayjs";
import { on as onEvent } from "@/eventBus.js";
import ActivityLogIcon from "./ActivityLogIcon.vue";
import { resolveActivityStyle, type ActivityStyleVariant } from "./ActivityLogStyle";

interface ActivityCauser {
  first_name: string;
  last_name: string;
}

interface Activity {
  id: number;
  description: string;
  created_at: string;
  causer: ActivityCauser | null;
}

const props = withDefaults(
  defineProps<{
    subject: string;
    styleVariant?: ActivityStyleVariant;
  }>(),
  {
    styleVariant: "default",
  }
);

const activityLog = ref<Activity[]>([]);

async function fetchActivityLog(): Promise<void> {
  try {
    const { data } = await axios.get("/backoffice/api/activity-log/", {
      params: {
        subject: props.subject,
        sort: "-created_at",
      },
    });

    activityLog.value = data.data;
  } catch (error) {
    console.log(error);
  }
}

onMounted(() => {
  fetchActivityLog();
  onEvent("refresh-activity-log", fetchActivityLog);
});
</script>

<template>
  <ul role="list" class="list-unstyled">
    <li v-for="(activity, i) in activityLog" :key="activity.id">
      <div :class="['position-relative', { 'pb-4': i < activityLog.length - 1 }]">
        <span v-if="i < activityLog.length - 1" class="feed position-absolute" aria-hidden="true"></span>
        <div class="position-relative d-flex align-items-center">
          <div>
            <ActivityLogIcon v-bind="resolveActivityStyle(props.styleVariant, activity.description)" />
          </div>
          <div class="d-flex min-w-0 flex-grow-1 align-items-center justify-content-between pt-1 ml-2">
            <div>
              <p class="text-sm text-muted mb-0">
                {{ activity.description }}
              </p>
              <template v-if="activity.causer">
                <p class="text-sm mb-0">
                  by <span class="tw:font-bold">{{ activity.causer.first_name }} {{ activity.causer.last_name }}</span>
                </p>
              </template>
            </div>
            <div class="whitespace-nowrap text-right text-sm text-muted">
              <time :datetime="activity.created_at">{{ dayjs(activity.created_at).format('lll') }}</time>
            </div>
          </div>
        </div>
      </div>
    </li>
  </ul>
</template>

<style scoped>
.feed {
  background-color: #E5E7EB;
  width: 0.125rem;
  height: 100%;
  margin-left: -1px;
  top: 1rem;
  left: 1rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
</style>
